import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { ChangeEvent, MouseEvent, useEffect, useState } from 'react'
import { Eye, EyeOff } from 'react-feather'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import { postRequest } from 'src/lib/core/api-client'
import { getNewUserDetails } from 'src/lib/core/api-methods'
import { useLocale } from 'src/lib/hooks/useLocal'
import { apiURI } from 'src/lib/utils/constants'
import Alerts from '../common/feedback-and-dialog/Alerts'
import InputTextField from '../common/form-elements/InputTextField'
import { regexPatterns } from 'src/lib/utils/validations'

interface PasswordState {
  value: string
  showValue: boolean
}

const SetPassword = () => {
  const { t } = useLocale()
  const router = useRouter()
  const { setLoaderProperties } = useLoader()

  const [password, setPassword] = useState<PasswordState>({ value: '', showValue: false })
  const [repassword, setRePassword] = useState<PasswordState>({ value: '', showValue: false })
  const [passwordInvalidMessage, setPasswordsInvalidMessage] = useState<string>('')
  const [openAlert, setOpenAlert] = useState<string>('')
  const [alertMessage, setAlertMessage] = useState<string>('')
  const [pageExpired, setPageExpired] = useState<boolean>(false)

  const [email, setEmail] = useState<string>('')

  const { token } = router.query

  const inputStyle = {
    style: {
      marginLeft: -16,
      marginRight: -8,
      paddingLeft: 16,
      paddingRight: 14,
      width: '104%',
      borderTopRightRadius: '0px',
      borderBottomRightRadius: '0px',
      fontSize: 14
    }
  }

  useEffect(() => {
    if (token && typeof token === 'string') {
      getUserContext(token)
    }
  }, [token])

  const getUserContext = async (token: string) => {
    try {
      setLoaderProperties({ isLoaderVisible: true, message: 'Loading, Please wait ...' })
      const response: any = await getNewUserDetails(token)
      setEmail(response?.data?.data?.oid)
      setPageExpired(response?.data?.data?.expired)
      setLoaderProperties({ isLoaderVisible: false })
    } catch (error: any) {
      console.error(error)
      alert('The link is invalid or expired.')
      router.push({
        pathname: '/auth/email-expired',
        query: { email }
      })
    } finally {
      setLoaderProperties({ isLoaderVisible: false })
    }
  }

  const handlePasswordChange = (type: 'password' | 'repassword') => (event: ChangeEvent<HTMLInputElement>) => {
    switch (type) {
      case 'password':
        setPassword({ ...password, value: event.target.value })
        return
      case 'repassword':
        setRePassword({ ...repassword, value: event.target.value })
        return
      default:
        return
    }
  }

  const handleClickShowPassword = (type: 'password' | 'repassword') => (event: MouseEvent<HTMLButtonElement>) => {
    switch (type) {
      case 'password':
        setPassword({ ...password, showValue: !password.showValue })
        return
      case 'repassword':
        setRePassword({ ...repassword, showValue: !repassword.showValue })
        return
      default:
        return
    }
  }

  const handleAlerts = (severity: string, message?: string) => {
    setOpenAlert(severity)
    message && setAlertMessage(message)
  }

  const handleCloseAlert = (_event: any, reason: string) => {
    if (reason === 'clickaway') {
      return
    }
    handleAlerts('')
  }

  const handleCreatePassword = async () => {
    if (password.value !== repassword.value) {
      setPasswordsInvalidMessage(t('passwords_not_match'))
      return
    }
    if (!password.value.match(regexPatterns.passwordPattern)) {
      setPasswordsInvalidMessage(t('password_wrong_format'))
      return
    }

    const payload = {
      invitation_token: token,
      password: password.value.trim()
    }
    try {
      await postRequest('/merchant-invitation/accept', apiURI.AUTH_ENGINE, payload)
      setOpenAlert('success')
      router.replace('/auth/login')
    } catch (e) {
      console.error(e)
      alert('The link is invalid or expired.')
      router.replace('/auth/login')
      setOpenAlert('error')
    }
  }

  // 24px
  return (
    <>
      {pageExpired ? (
        <div className='flex flex-col items-center gap-3'>
          <p className='text-[#ffffff] text-[18px] font-semibold'>{t('link_expired')}</p>
          <p className='text-[#B6B6BC] text-sm'>{t('link_valid_15_mins')}</p>
        </div>
      ) : (
        <div className='-mt-8'>
          <div className='flex items-center justify-center mb-6 text-sm'>
            <p className='text-[#e0e0e0]'>{email}</p>&nbsp;
            <p className='font-semibold text-gray-lightest border-b-[1.5px] border-[#949494]'>
              <Link href='/auth/login'>{t('not_you')}</Link>
            </p>
          </div>
          <form
            noValidate
            autoComplete='off'
            onSubmit={e => e.preventDefault()}
            className='flex flex-col border w-[26.5rem] bg-[#FFFFFF] rounded-2xl p-4 gap-4'
          >
            <div className=''>
              <InputTextField
                label={t('enter_password')}
                inputProps={inputStyle}
                textFieldId='password'
                handleChange={handlePasswordChange('password')}
                inputType={password.showValue ? 'text ' : 'password'}
                inputValue={password.value}
                inputPlaceholder={t('password_placeholder')}
                endIcon={
                  <button
                    className={`w-10 h-9 flex justify-center items-center rounded-br-lg rounded-tr-lg
            transition duration-300 ${
              password.showValue ? 'bg-gray-light' : 'bg-gray-lightest'
            } hover:bg-[#e0e0e0] -mr-2`}
                    aria-label='toggle password visibility'
                    onClick={handleClickShowPassword('password')}
                  >
                    {!password.showValue ? <EyeOff className='w-5 h-5' /> : <Eye className='w-5 h-5' />}
                  </button>
                }
              />
            </div>
            <div className=''>
              <InputTextField
                label={t('renter_password')}
                inputProps={inputStyle}
                textFieldId='repassword'
                inputValue={repassword.value}
                inputType={repassword.showValue ? 'text ' : 'password'}
                handleChange={handlePasswordChange('repassword')}
                inputPlaceholder={t('repassword_placeholder')}
                endIcon={
                  <button
                    className={`w-10 h-9 flex justify-center items-center rounded-br-lg rounded-tr-lg
            transition duration-300 ${
              repassword.showValue ? 'bg-[#f0f0f0]' : 'bg-gray-lightest'
            } hover:bg-[#e0e0e0] -mr-2`}
                    aria-label='toggle password visibility'
                    onClick={handleClickShowPassword('repassword')}
                  >
                    {!repassword.showValue ? <EyeOff className='w-5 h-5' /> : <Eye className='w-5 h-5' />}
                  </button>
                }
              />
            </div>
            {passwordInvalidMessage && <p className='text-sm text-center text-[#f44336]'>{passwordInvalidMessage}</p>}

            <div className=''>
              <button
                disabled={!Boolean(password.value && repassword.value)}
                type='submit'
                className='w-full h-8 text-sm font-semibold  rounded-lg bg-primary-dark text-[#ffffff]'
                onClick={handleCreatePassword}
              >
                {t('create_password')}
              </button>
            </div>
          </form>
        </div>
      )}
      {openAlert.length ? (
        <Alerts
          open={true}
          duration={2000}
          closeHandler={handleCloseAlert}
          severity={openAlert}
          message={alertMessage}
        />
      ) : null}
    </>
  )
}

export default SetPassword
